import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800' };

function WhatsAppCampaigns({ user, onLogout }) {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    template_name: '',
    recipient_type: 'all',
    tag: '',
    segment: '',
    schedule_type: 'immediate'
  });

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchStats();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/campaigns/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const campaignData = {
        name: formData.name,
        template_name: formData.template_name,
        recipient_type: formData.recipient_type,
        schedule_type: formData.schedule_type
      };

      if (formData.recipient_type === 'tag') {
        campaignData.recipient_filter = { tag: formData.tag };
      } else if (formData.recipient_type === 'segment') {
        campaignData.recipient_filter = { segment: formData.segment };
      }

      await axios.post(`${API}/api/whatsapp/campaigns`, campaignData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Campaign created and started!');
      setShowCreate(false);
      setFormData({
        name: '',
        template_name: '',
        recipient_type: 'all',
        tag: '',
        segment: '',
        schedule_type: 'immediate'
      });
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleViewCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCampaign(response.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRetryCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/whatsapp/campaigns/${campaignId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('✅ Retry started for failed messages!');
      fetchCampaigns();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      running: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      paused: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
              📢 WhatsApp Campaigns
            </h1>
            <p className="text-gray-600">Create and manage bulk messaging campaigns</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            ➕ Create Campaign
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Total Campaigns</p>
            <p className="text-2xl font-bold" style={{ color: COLORS.primary }}>
              {stats.total_campaigns || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Running</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.by_status?.running || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {stats.by_status?.completed || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Messages Sent</p>
            <p className="text-2xl font-bold text-purple-600">
              {stats.total_messages_sent || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Delivery Rate</p>
            <p className="text-2xl font-bold text-green-600">
              {stats.delivery_rate || 0}%
            </p>
          </div>
        </div>

        {/* Create Campaign Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                Create New Campaign
              </h2>

              <form onSubmit={handleCreateCampaign} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Campaign Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Monthly Newsletter"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Select Template *</label>
                  <select
                    value={formData.template_name}
                    onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Choose template</option>
                    {templates.filter(t => t.status === 'APPROVED').map((template) => (
                      <option key={template.id} value={template.name}>
                        {template.name} ({template.language})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Only approved templates are shown. {templates.filter(t => t.status === 'APPROVED').length} available.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Recipients *</label>
                  <select
                    value={formData.recipient_type}
                    onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="all">All Contacts</option>
                    <option value="tag">By Tag</option>
                    <option value="segment">By Segment</option>
                  </select>
                </div>

                {formData.recipient_type === 'tag' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Tag</label>
                    <input
                      type="text"
                      value={formData.tag}
                      onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., vip, customer"
                    />
                  </div>
                )}

                {formData.recipient_type === 'segment' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Segment</label>
                    <input
                      type="text"
                      value={formData.segment}
                      onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., internal, external"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Schedule</label>
                  <select
                    value={formData.schedule_type}
                    onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="immediate">Send Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                  </select>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Important:</strong> Campaigns will be sent at 1 message/second to avoid rate limits.
                    Large campaigns may take time to complete.
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? '⏳ Creating...' : '📤 Create & Send'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreate(false)}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Campaign Details Modal */}
        {selectedCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>
                  {selectedCampaign.name}
                </h2>
                <button
                  onClick={() => setSelectedCampaign(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-2 py-1 rounded text-sm ${getStatusBadge(selectedCampaign.status)}`}>
                    {selectedCampaign.status}
                  </span>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Sent</p>
                  <p className="text-xl font-bold text-green-600">{selectedCampaign.sent_count}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Failed</p>
                  <p className="text-xl font-bold text-red-600">{selectedCampaign.failed_count}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Template</p>
                  <p className="text-sm font-medium">{selectedCampaign.template_name}</p>
                </div>
              </div>

              {selectedCampaign.delivery_reports && selectedCampaign.delivery_reports.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Delivery Reports</h3>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Phone</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCampaign.delivery_reports.map((report, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-mono">{report.phone_number}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                report.status === 'sent' ? 'bg-green-100 text-green-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {report.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {report.sent_at ? new Date(report.sent_at).toLocaleString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {selectedCampaign.failed_count > 0 && (
                <button
                  onClick={() => handleRetryCampaign(selectedCampaign.id)}
                  className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  🔄 Retry Failed Messages
                </button>
              )}
            </div>
          </div>
        )}

        {/* Campaigns List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Campaign</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Template</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Recipients</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Sent/Failed</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    <p className="text-4xl mb-2">📢</p>
                    <p>No campaigns yet</p>
                    <p className="text-sm mt-2">Click "Create Campaign" to start your first bulk messaging campaign</p>
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{campaign.name}</td>
                    <td className="px-4 py-3 text-sm">{campaign.template_name}</td>
                    <td className="px-4 py-3 text-sm">{campaign.recipient_type}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-green-600 font-medium">{campaign.sent_count}</span>
                      {' / '}
                      <span className="text-red-600">{campaign.failed_count}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewCampaign(campaign.id)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppCampaigns;
